import { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';
import { signOut } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import { refreshPlan, usePlan } from '../hooks/usePlan.ts';
import { showPaywall } from '../lib/plan.ts';

interface LayoutProps {
  onDisconnect: () => void;
}

function SidebarLink({ to, icon, label }: Readonly<{ to: string; icon: string; label: string }>) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
          isActive
            ? 'bg-(--color-accent)/15 text-(--color-accent)'
            : 'text-(--color-text-secondary) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary)'
        }`
      }
    >
      <span className="text-lg">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

export function Layout({ onDisconnect }: Readonly<LayoutProps>) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const { isPro, isFree } = usePlan();

  // The plan lives on the server (store purchases, comp grants): load it fresh.
  useEffect(() => {
    void refreshPlan();
  }, []);

  const navItems = [
    { to: '/rides', icon: '\u{1F4CA}', label: t('nav.rides') },
    { to: '/', icon: '\u{1F4AC}', label: t('nav.chat') },
    { to: '/fuel', icon: '\u{1F33F}', label: t('nav.fuel') },
    { to: '/blocks', icon: '\u{1F4C5}', label: t('nav.blocks') },
    { to: '/train', icon: '\u{1F6B4}', label: t('nav.train') },
  ];

  const handleDisconnect = () => {
    if (globalThis.confirm('Sign out?')) {
      void signOut().then(onDisconnect);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar.
          ``overflow-y-auto`` lets the bottom group (Profile / lang
          switcher / sign out) stay reachable on short viewports —
          landscape mobile is ~414px tall and the full sidebar
          previously didn't fit, leaving the bottom items hidden
          off-screen with no way to scroll. */}
      <aside className="hidden md:flex flex-col w-56 bg-(--color-bg-secondary) border-r border-(--color-border) p-4 shrink-0 overflow-y-auto">
        <div className="flex items-center gap-2 px-4 mb-8">
          <span className="text-2xl">{'\u{1F6B4}'}</span>
          <span className="text-lg font-bold text-(--color-accent)">{t('nav.appName')}</span>
        </div>
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <SidebarLink key={item.to} {...item} />
          ))}
        </nav>
        <div className="mt-auto pt-4 border-t border-(--color-border) flex flex-col gap-2">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-(--color-accent)/15 text-(--color-accent)'
                  : 'text-(--color-text-secondary) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary)'
              }`
            }
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />
            ) : (
              <span className="w-6 h-6 rounded-full bg-(--color-accent)/20 flex items-center justify-center text-[10px] font-bold text-(--color-accent)">
                {initials}
              </span>
            )}
            <span className="truncate">{user?.name ?? 'Profile'}</span>
            {isPro && (
              <span className="ml-auto rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider bg-amber-400/15 text-amber-300">
                {t('plan.badgePro')}
              </span>
            )}
          </NavLink>
          {isFree && (
            <button
              type="button"
              onClick={() => showPaywall()}
              className="mx-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-left text-xs font-semibold text-amber-300 hover:bg-amber-400/20 transition-colors"
            >
              {t('plan.getPro')}
            </button>
          )}
          <div className="flex items-center justify-between px-2">
            <LanguageSwitcher />
            <button
              onClick={handleDisconnect}
              title="Sign out"
              className="text-(--color-text-secondary) hover:text-red-400 transition-colors text-xs px-2 py-1 rounded-lg hover:bg-(--color-bg-tertiary)"
            >
              {'\u{1F6AA}'}
            </button>
          </div>
          <div className="flex gap-3 px-2 text-[11px] text-(--color-text-secondary)">
            <a href="/privacy" className="hover:text-(--color-text-primary)">{t('plan.legal.privacy')}</a>
            <a href="/terms" className="hover:text-(--color-text-primary)">{t('plan.legal.terms')}</a>
            <a href="/support" className="hover:text-(--color-text-primary)">{t('plan.legal.support')}</a>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-(--color-bg-secondary) border-t border-(--color-border) flex justify-around px-2 py-2 z-40">
        {navItems.map(({ to, icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                isActive
                  ? 'text-(--color-accent) bg-(--color-accent)/10'
                  : 'text-(--color-text-secondary)'
              }`
            }
          >
            <span className="text-xl">{icon}</span>
            <span>{label}</span>
          </NavLink>
        ))}
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-medium transition-colors ${
              isActive
                ? 'text-(--color-accent) bg-(--color-accent)/10'
                : 'text-(--color-text-secondary)'
            }`
          }
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-5 h-5 rounded-full" />
          ) : (
            <span className="w-5 h-5 rounded-full bg-(--color-accent)/20 flex items-center justify-center text-[8px] font-bold text-(--color-accent)">
              {initials}
            </span>
          )}
          <span>Profile</span>
        </NavLink>
      </nav>
    </div>
  );
}
